// Mobile Overlay Module - Handles mobile device detection and coming soon overlay
// Feature flag controlled for easy enabling/disabling

import { MOBILE_FUNCTIONALITY_ENABLED, MOBILE_OVERLAY_CONFIG } from '../config/mobile-config.js';

// Configuration object for mobile overlay
const MOBILE_CONFIG = {
    // Feature flag - set to false when mobile functionality is ready
    enabled: !MOBILE_FUNCTIONALITY_ENABLED,
    
    // Screen size breakpoints for mobile detection
    mobileMaxWidth: MOBILE_OVERLAY_CONFIG.mobileBreakpoint,
    smallMobileMaxWidth: MOBILE_OVERLAY_CONFIG.smallMobileBreakpoint,
    
    // Touch detection enabled
    detectTouch: MOBILE_OVERLAY_CONFIG.enableTouchDetection,
    
    // User agent detection patterns
    mobileUserAgents: [
        /Android/i,
        /webOS/i,
        /iPhone/i,
        /iPad/i,
        /iPod/i,
        /BlackBerry/i,
        /Windows Phone/i,
        /Mobile/i,
        /mobile/i,
        /CriOS/i
    ],
    
    // Tablet detection patterns (tablets are allowed)
    tabletUserAgents: [
        /iPad/i,
        /Android(?=.*\b(tablet|pad)\b)/i,
        /Tablet/i,
        /tablet/i
    ],
    
    // Local storage key for user preference
    storageKey: MOBILE_OVERLAY_CONFIG.dismissalStorageKey,
    
    // Session storage key for current session
    sessionKey: MOBILE_OVERLAY_CONFIG.sessionStorageKey
};

// Mobile detection functions
const MobileDetection = {
    // Check if device is mobile based on screen size
    isMobileScreenSize() {
        return window.innerWidth <= MOBILE_CONFIG.mobileMaxWidth;
    },
    
    // Check if device is small mobile
    isSmallMobile() {
        return window.innerWidth <= MOBILE_CONFIG.smallMobileMaxWidth;
    },
    
    // Check if device has touch capability
    isTouchDevice() {
        if (!MOBILE_CONFIG.detectTouch) return false;
        
        return (
            'ontouchstart' in window ||
            navigator.maxTouchPoints > 0 ||
            navigator.msMaxTouchPoints > 0 ||
            window.DocumentTouch && document instanceof DocumentTouch
        );
    },
    
    // Check user agent for mobile patterns
    isMobileUserAgent() {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        return MOBILE_CONFIG.mobileUserAgents.some(pattern => pattern.test(userAgent));
    },
    
    // Check if device is a tablet (tablets are allowed)
    isTablet() {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        return MOBILE_CONFIG.tabletUserAgents.some(pattern => pattern.test(userAgent));
    },
    
    // Comprehensive mobile detection
    isMobileDevice() {
        // If it's a tablet, allow it
        if (this.isTablet()) {
            return false;
        }
        
        // Check multiple criteria
        const screenSize = this.isMobileScreenSize();
        const userAgent = this.isMobileUserAgent();
        const touch = this.isTouchDevice();
        
        // Consider it mobile if it meets screen size criteria AND (user agent OR touch)
        return screenSize && (userAgent || touch);
    },
    
    // Get device type for logging
    getDeviceType() {
        if (this.isTablet()) return 'tablet';
        if (this.isMobileDevice()) return 'mobile';
        return 'desktop';
    }
};

// Storage utilities
const StorageUtils = {
    // Check if user has permanently dismissed the overlay
    isPermanentlyDismissed() {
        try {
            return localStorage.getItem(MOBILE_CONFIG.storageKey) === 'true';
        } catch (e) {
            console.warn('LocalStorage not available:', e);
            return false;
        }
    },
    
    // Check if user has dismissed for this session
    isSessionDismissed() {
        try {
            return sessionStorage.getItem(MOBILE_CONFIG.sessionKey) === 'true';
        } catch (e) {
            console.warn('SessionStorage not available:', e);
            return false;
        }
    },
    
    // Set permanent dismissal
    setPermanentDismissal() {
        try {
            localStorage.setItem(MOBILE_CONFIG.storageKey, 'true');
        } catch (e) {
            console.warn('Could not set localStorage:', e);
        }
    },
    
    // Set session dismissal
    setSessionDismissal() {
        try {
            sessionStorage.setItem(MOBILE_CONFIG.sessionKey, 'true');
        } catch (e) {
            console.warn('Could not set sessionStorage:', e);
        }
    },
    
    // Clear dismissal (for testing or re-enabling)
    clearDismissal() {
        try {
            localStorage.removeItem(MOBILE_CONFIG.storageKey);
            sessionStorage.removeItem(MOBILE_CONFIG.sessionKey);
        } catch (e) {
            console.warn('Could not clear storage:', e);
        }
    }
};

// Main mobile overlay class
class MobileOverlay {
    constructor() {
        this.overlay = null;
        this.closeBtn = null;
        this.tryAnywayBtn = null;
        this.isVisible = false;
        this.resizeTimeout = null;
        
        this.init();
    }
    
    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
            this.setup();
        }
    }
    
    setup() {
        // Get DOM elements
        this.overlay = document.getElementById('mobile-overlay');
        this.closeBtn = document.getElementById('mobile-overlay-close');
        this.tryAnywayBtn = document.getElementById('mobile-try-anyway');
        
        if (!this.overlay) {
            console.warn('Mobile overlay element not found');
            return;
        }
        
        // Set up event listeners
        this.setupEventListeners();
        
        // Initial check
        this.checkAndShowOverlay();
        
        // Add device type class to body for CSS targeting
        this.addDeviceClass();
        
        console.log('📱 Mobile overlay initialized');
        console.log('Device type:', MobileDetection.getDeviceType());
        console.log('Screen size:', `${window.innerWidth}x${window.innerHeight}`);
    }
    
    setupEventListeners() {
        // Close button - only allow on non-mobile (e.g. when testing); on mobile the button is hidden and we don't dismiss
        if (this.closeBtn) {
            this.closeBtn.addEventListener('click', () => {
                if (!MobileDetection.isMobileDevice()) this.hideOverlay(false);
            });
        }
        
        // Try anyway button (conditionally enabled)
        if (this.tryAnywayBtn && MOBILE_OVERLAY_CONFIG.allowTryAnyway) {
            this.tryAnywayBtn.addEventListener('click', () => {
                if (!MobileDetection.isMobileDevice()) this.hideOverlay(false);
            });
        }
        
        // Escape key - do not close on mobile so overlay cannot be bypassed
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isVisible && !MobileDetection.isMobileDevice()) {
                this.hideOverlay(false);
            }
        });
        
        // Window resize with debouncing
        window.addEventListener('resize', () => {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(() => {
                this.handleResize();
            }, 250);
        });
        
        // Orientation change
        window.addEventListener('orientationchange', () => {
            setTimeout(() => {
                this.handleResize();
            }, 500);
        });
    }
    
    addDeviceClass() {
        const deviceType = MobileDetection.getDeviceType();
        document.body.classList.add(`device-${deviceType}`);
        
        if (MobileDetection.isMobileDevice()) {
            document.body.classList.add('is-mobile-device');
        }
        
        if (!MOBILE_CONFIG.enabled) {
            document.body.classList.add('mobile-ready');
        }
    }
    
    checkAndShowOverlay() {
        // Feature flag check
        if (!MOBILE_CONFIG.enabled) {
            console.log('📱 Mobile overlay disabled by feature flag');
            return false;
        }
        
        // Check if mobile device
        if (!MobileDetection.isMobileDevice()) {
            console.log('📱 Not a mobile device, overlay not needed');
            return false;
        }
        
        // On mobile we always show the overlay (main app only); no dismissal is persisted.
        // Refresh will always show the overlay again.
        this.showOverlay();
        return true;
    }
    
    showOverlay() {
        if (!this.overlay || this.isVisible) return;
        
        console.log('📱 Showing mobile overlay');
        
        // Add classes and show
        document.body.classList.add('mobile-functionality-disabled');
        this.overlay.classList.add('show');
        this.isVisible = true;
        
        // Prevent body scroll
        document.body.style.overflow = 'hidden';
        
        // Focus management for accessibility
        this.overlay.focus();
        
        // Analytics/tracking could go here
        this.trackOverlayShown();
    }
    
    hideOverlay(permanent = false) {
        if (!this.overlay || !this.isVisible) return;
        
        console.log('📱 Hiding mobile overlay', permanent ? '(permanent)' : '(session)');
        
        // Hide overlay
        this.overlay.classList.remove('show');
        document.body.classList.remove('mobile-functionality-disabled');
        this.isVisible = false;
        
        // Restore body scroll
        document.body.style.overflow = '';
        
        // Set dismissal preference
        if (permanent) {
            StorageUtils.setPermanentDismissal();
        } else {
            StorageUtils.setSessionDismissal();
        }
        
        // Analytics/tracking could go here
        this.trackOverlayDismissed(permanent);
    }
    
    handleResize() {
        // Re-check if overlay should be shown after resize
        if (!this.isVisible && MobileDetection.isMobileDevice()) {
            this.checkAndShowOverlay();
        } else if (this.isVisible && !MobileDetection.isMobileDevice()) {
            this.hideOverlay(false);
        }
    }
    
    // Public methods for external control
    forceShow() {
        this.showOverlay();
    }
    
    forceHide(permanent = false) {
        this.hideOverlay(permanent);
    }
    
    isCurrentlyVisible() {
        return this.isVisible;
    }
    
    resetDismissal() {
        StorageUtils.clearDismissal();
        console.log('📱 Mobile overlay dismissal reset');
    }
    
    // Analytics/tracking methods (placeholders)
    trackOverlayShown() {
        // Add your analytics tracking here
        console.log('📊 Mobile overlay shown');
    }
    
    trackOverlayDismissed(permanent) {
        // Add your analytics tracking here
        console.log('📊 Mobile overlay dismissed:', permanent ? 'permanent' : 'session');
    }
}

// Global utility functions
window.MobileOverlayUtils = {
    // Check if mobile functionality is enabled
    isMobileReady() {
        return !MOBILE_CONFIG.enabled;
    },
    
    // Enable mobile functionality (removes overlay permanently)
    enableMobileFunctionality() {
        MOBILE_CONFIG.enabled = false;
        document.body.classList.add('mobile-ready');
        
        if (window.mobileOverlayInstance) {
            window.mobileOverlayInstance.forceHide(true);
        }
        
        console.log('📱 Mobile functionality enabled');
    },
    
    // Disable mobile functionality (shows overlay for mobile devices)
    disableMobileFunctionality() {
        MOBILE_CONFIG.enabled = true;
        document.body.classList.remove('mobile-ready');
        
        if (window.mobileOverlayInstance && MobileDetection.isMobileDevice()) {
            window.mobileOverlayInstance.resetDismissal();
            window.mobileOverlayInstance.forceShow();
        }
        
        console.log('📱 Mobile functionality disabled');
    },
    
    // Get current device information
    getDeviceInfo() {
        return {
            type: MobileDetection.getDeviceType(),
            isMobile: MobileDetection.isMobileDevice(),
            isTablet: MobileDetection.isTablet(),
            screenSize: `${window.innerWidth}x${window.innerHeight}`,
            userAgent: navigator.userAgent,
            touchSupport: MobileDetection.isTouchDevice(),
            overlayEnabled: MOBILE_CONFIG.enabled,
            overlayVisible: window.mobileOverlayInstance?.isCurrentlyVisible() || false
        };
    },
    
    // Reset overlay dismissal (for testing)
    resetOverlay() {
        if (window.mobileOverlayInstance) {
            window.mobileOverlayInstance.resetDismissal();
        }
    }
};

// Initialize mobile overlay
function initializeMobileOverlay() {
    window.mobileOverlayInstance = new MobileOverlay();
    return window.mobileOverlayInstance;
}

// Export for use in main.js
export { initializeMobileOverlay, MobileDetection, MOBILE_CONFIG };

// Auto-initialize if script is loaded directly
if (typeof window !== 'undefined') {
    initializeMobileOverlay();
} 